import {
  Avatar,
  Button,
  Card,
  Drawer,
  Empty,
  Icons,
  notification,
  Spin,
  Table,
  Typography,
} from '@pankod/refine-antd';
import { Grid } from 'antd';
import { useEffect, useState } from 'react';
import { EInvoiceType, getSale, getSaleInvoice } from 'services/sale';
import { previewPdfFromBase64 } from 'utilities/shared';

interface OrderShowDrawerProps {
  open: boolean;
  onClose: () => void;
  orderId: string | null;
  width?: number;
  viewType?: 'restaurant-sales-view' | 'waiter-view';
}

type OrderDetail = {
  id: string;
  itemCode: string;
  itemName: string;
  unitPrice: number;
  quantity: number;
  price: number;
  photo: string | null;
};

export const OrderShowDrawer: React.FC<OrderShowDrawerProps> = ({
  open,
  onClose,
  orderId,
  width = 378,
  viewType = 'restaurant-sales-view', // Default to restaurant-sales-view
}) => {
  const breakpoint = Grid.useBreakpoint();
  const isMobile = !breakpoint.sm;
  const [loading, setLoading] = useState<boolean>(false);
  const [invoiceLoading, setInvoiceLoading] = useState<boolean>(false);
  const [orderDetails, setOrderDetails] = useState<OrderDetail[]>([]);

  useEffect(() => {
    async function fetchSale(saleId: string) {
      try {
        setLoading(true);
        const response = await getSale(saleId);
        const salesProducts = response.salesProductsCopy ?? [];
        const orderDetails = salesProducts.map((product) => ({
          id: product.id,
          itemCode: product.productVariant.sku,
          itemName: product.productVariant.name,
          unitPrice: product.unitPrice,
          quantity: product.quantity,
          price: product.subTotal,
          photo: product.productVariant?.photo?.url ?? null,
        }));
        setOrderDetails(orderDetails);
      } catch (err: any) {
        notification.error({
          message: 'Failed',
          description: err?.message || 'Failed to fetch order details',
        });
      } finally {
        setLoading(false);
      }
    }

    if (open && orderId) {
      fetchSale(orderId);
    }
  }, [open, orderId]);

  const handleClose = () => {
    setOrderDetails([]);
    onClose();
  };

  const handleGenerateInvoice = async (id: string) => {
    try {
      setInvoiceLoading(true);
      const invoiceString = await getSaleInvoice(id, EInvoiceType.Sales);
      previewPdfFromBase64(invoiceString);
    } catch (err: any) {
      notification.error({
        message: 'Failed',
        description: err?.message || 'Failed to generate invoice',
      });
    } finally {
      setInvoiceLoading(false);
    }
  };

  const footer = (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 30 }}>
      <Button onClick={onClose} style={{ flex: 1 }}>
        Cancel
      </Button>
      <Button
        type="primary"
        onClick={() => handleGenerateInvoice(orderId!)}
        style={{ flex: 1 }}
        disabled={orderDetails.length === 0 || loading}
        icon={<Icons.FilePdfOutlined />}
        loading={invoiceLoading}
      >
        Invoice
      </Button>
    </div>
  );

  const renderRestaurantSalesView = () => (
    <>
      <Table<OrderDetail>
        bordered
        rowKey="id"
        dataSource={orderDetails}
        pagination={false}
        scroll={{ x: true }}
      >
        <Table.Column<OrderDetail> dataIndex="itemCode" title="Item Code" />
        <Table.Column<OrderDetail> dataIndex="itemName" title="Item Name" />
        <Table.Column<OrderDetail> dataIndex="unitPrice" title="Unit Price" />
        <Table.Column<OrderDetail> dataIndex="quantity" title="Quantity" />
        <Table.Column<OrderDetail> dataIndex="price" title="Price" />
      </Table>

      <p style={{ fontWeight: 'bold', textAlign: 'right', marginTop: 10 }}>
        Total: {orderDetails.reduce((sum, item) => sum + item.price, 0)}
      </p>
    </>
  );

  const renderWaiterView = () => {
    // Calculate the total price
    const totalPrice = orderDetails.reduce((sum, item) => sum + item.price, 0);

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {orderDetails.map((item) => (
          <Card
            key={item.id}
            style={{
              width: '100%',
              border: 'none',
              // boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.1)',
              // borderRadius: '8px',
              borderBottom: '1px solid #DEEEF8',
              borderRadius: 'none',
            }}
            bodyStyle={{ padding: 12, borderRadius: 'none' }}
          >
            <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
              <Avatar
                src={item.photo || 'https://via.placeholder.com/80'}
                alt={item.itemName}
                style={{
                  width: 60,
                  height: 60,
                  borderRadius: '8px',
                  objectFit: 'cover',
                }}
                shape="square"
                size={60}
              />
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography.Text
                      title={item.itemName}
                      ellipsis
                      style={{
                        display: '-webkit-box',
                        WebkitLineClamp: 1,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'normal',
                        lineHeight: '1.5',
                        maxWidth: '100%',
                      }}
                    >
                      {item.itemName}
                    </Typography.Text>
                    <Typography.Text>{item.quantity}</Typography.Text>
                  </div>
                  {/* <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography.Text type="secondary">{item.itemCode}</Typography.Text>
                  </div> */}
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography.Text type="secondary">{item.unitPrice} BDT</Typography.Text>
                    <Typography.Text type="secondary">{item.price} BDT</Typography.Text>
                  </div>
                </div>
              </div>
            </div>
          </Card>
        ))}

        {/* Display Total Price */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            // padding: '12px 16px',
            // backgroundColor: '#f5f5f5',
            // borderRadius: '8px',
          }}
        >
          <Typography.Text strong style={{ fontSize: 16 }}>
            Total: {totalPrice} BDT
          </Typography.Text>
        </div>
      </div>
    );
  };

  return (
    <Drawer
      title={`Order Details - ${orderId || ''}`}
      placement="right"
      onClose={handleClose}
      visible={open}
      width={isMobile ? '100%' : width}
      footer={footer}
    >
      {loading ? (
        <Spin
          spinning
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '60vh',
          }}
        />
      ) : orderDetails.length > 0 ? (
        viewType === 'restaurant-sales-view' ? (
          renderRestaurantSalesView()
        ) : (
          renderWaiterView()
        )
      ) : (
        <Empty description="No order details available." style={{ marginTop: 100 }} />
      )}
    </Drawer>
  );
};
